// vehicleCosts.js — PROJEKCIA nákladov na vozidlo (UI číta odtiaľto).
// Spec: MasterProject/feature-ideas/ideas/todue-kniha-jazd-SPEC-v2.md §2 naklad_vozidla.
// Právne: § 85n ods. 6 písm. e (usmernenie FS 3/DPH/2025/MU) — evidencia VŠETKÝCH
// tovarov/služieb na prevádzku vozidla (nielen PHM), po položkách, s cenou bez DPH a dátumom.
//
// Rovnaká architektúra ako vehicleTrips: autoritatívny dôkaz = vehicle_trip_ledger
// (cost_recorded/cost_voided/cost_corrected). vehicle_costs je odvodená projekcia,
// budovaná JEDNOU čistou fold funkciou zdieľanou zápisom aj rebuildom → nemôžu sa rozísť.
// Zápis (ledger event + projekcia) je ATOMICKY v jednej db.transaction.
//
// ODPOČET DPH (regime-aware): deduction_pct sa fixuje pri zápise podľa režimu vozidla
// v deň dokladu. 100 % len ak je vozidlo v ten deň 100%-eligible (checkRegime100Eligibility
// s atDate=doc_date); inak 50 % (poradca, príklad č. 18: 100 % len z položiek priradených
// ku konkrétnemu vozidlu — priradenie je tu inherentné cez vehicle_id). Zafixovaný snapshot
// v evente = dôkaz rozhodnutia v tom čase (neskoršia zmena režimu ho spätne neprepíše).
import { db } from './db';
import {
  appendTripEvent, listTripLedger, tripLedgerEventId, TRIP_LEDGER_EVENT_TYPES,
} from './vehicleLedger';
import { regimeAt, checkRegime100Eligibility, dateOnly } from './vehicles';

const COST_PROJECTION_EVENTS = new Set(['cost_recorded', 'cost_voided', 'cost_corrected']);

export const COST_TYPES = ['phm', 'servis', 'pneu', 'parkovne', 'myto', 'poistenie', 'ine'];

// Vecné polia nákladu prenášané z payloadu do projekcie (whitelist).
const COST_VALUE_FIELDS = [
  'cost_type', 'specification',
  'price_base_cents', 'vat_rate', 'vat_cents', 'deduction_pct',
  'doc_number', 'doc_date', 'attachment_hash',
  'trip_id', 'liters',
];

function pickCostValues(src) {
  const out = {};
  if (!src || typeof src !== 'object') return out;
  for (const k of COST_VALUE_FIELDS) if (src[k] !== undefined) out[k] = src[k];
  return out;
}

function emptyCostRow(id, vehicleId) {
  return {
    id, vehicle_id: vehicleId, seq_no: null,
    cost_type: null, specification: null,
    price_base_cents: null, vat_rate: null, vat_cents: null, deduction_pct: null,
    doc_number: null, doc_date: null, attachment_hash: null,
    trip_id: null, liters: null,
    status: 'active', corrects_id: null, void_reason: null,
    period_key: null, closed_at: null,
    provenance: null, custom: null,
    created_at: null, updated_at: null, rev: 0,
    deleted_at: null, schema_version: 1, obligation_pack_version: 0,
  };
}

function stampRow(row, ev) {
  row.rev = (row.rev || 0) + 1;
  if (!row.created_at) row.created_at = ev.created_at;
  row.updated_at = ev.created_at;
}

/**
 * resolveCostDeduction(vehicle, docDate) — efektívny odpočet DPH nákladu (50/100)
 * podľa režimu vozidla v deň dokladu. 100 % len ak vozidlo je v ten deň 100%-eligible;
 * inak 50 % (default paušál). Čistá funkcia (vstup = snapshot vehicle + dátum).
 */
export function resolveCostDeduction(vehicle, docDate) {
  const iv = regimeAt(vehicle, docDate);
  if (!iv || iv.dph_rezim !== 100) return 50;
  const elig = checkRegime100Eligibility(vehicle, iv, { atDate: docDate });
  return elig.eligible ? 100 : 50;
}

/**
 * foldCostEvents(events) — ČISTÁ deriv: ledger eventy JEDNÉHO vozidla (v seq poradí)
 * → Map<cost_id, projekčný riadok vehicle_costs>. seq_no = neprerušená rada nákladov
 * per vozidlo (prideľuje sa pri recorded/corrected; stornovaný si číslo ponechá).
 */
export function foldCostEvents(events) {
  const vehicleIds = new Set(events.map((e) => e.vehicle_id));
  if (vehicleIds.size > 1) {
    throw new Error(`[v2/vehicleCosts] foldCostEvents dostal eventy ${vehicleIds.size} vozidiel — fold je per-vozidlo`);
  }
  const sorted = [...events].sort((a, b) => a.seq - b.seq);
  const rows = new Map();
  let nextSeqNo = 0;

  for (const ev of sorted) {
    if (!COST_PROJECTION_EVENTS.has(ev.event_type)) continue;
    const payload = ev.payload || {};

    if (ev.event_type === 'cost_recorded') {
      if (rows.has(ev.trip_id)) continue; // recorded na existujúci cost = no-op (nezmaž seq_no)
      const row = emptyCostRow(ev.trip_id, ev.vehicle_id);
      Object.assign(row, pickCostValues(payload));
      row.provenance = payload.provenance ?? null;
      row.status = 'active';
      nextSeqNo += 1; row.seq_no = nextSeqNo;
      stampRow(row, ev);
      rows.set(ev.trip_id, row);
      continue;
    }

    if (ev.event_type === 'cost_voided') {
      const row = rows.get(ev.trip_id);
      if (!row) continue;
      row.status = 'voided';
      row.void_reason = payload.reason ?? null;
      stampRow(row, ev);
      continue;
    }

    if (ev.event_type === 'cost_corrected') {
      if (rows.has(ev.trip_id)) continue; // opravný náklad = NOVÉ id (nikdy neprepíš existujúci)
      const row = emptyCostRow(ev.trip_id, ev.vehicle_id);
      Object.assign(row, pickCostValues(payload));
      if (payload.provenance !== undefined) row.provenance = payload.provenance;
      row.corrects_id = payload.corrects_id ?? null;
      row.status = 'active';
      nextSeqNo += 1; row.seq_no = nextSeqNo;
      stampRow(row, ev);
      rows.set(ev.trip_id, row);
      continue;
    }
  }
  return rows;
}

function rowDiffers(next, prev) {
  if (!prev) return true;
  for (const k of Object.keys(next)) {
    if (JSON.stringify(next[k] ?? null) !== JSON.stringify(prev[k] ?? null)) return true;
  }
  return false;
}

// Prehrá ledger vozidla → upsertne zmenené projekčné riadky nákladov (rovnaký ledger
// nesie trip aj cost eventy; fold si filtruje len cost_*). `tx` = transakčný handle.
async function syncProjectionFromLedger(vehicleId, tx) {
  const events = await listTripLedger(vehicleId, tx);
  const folded = foldCostEvents(events);
  for (const [id, row] of folded) {
    const prev = await tx.vehicle_costs.get(id);
    if (rowDiffers(row, prev)) await tx.vehicle_costs.put(row);
  }
  return folded;
}

/**
 * recordCostEvent — JEDINÁ zápisová cesta pre náklady. Ledger event + projekcia
 * vehicle_costs ATOMICKY. Pri cost_recorded/cost_corrected DOPOČÍTA deduction_pct z
 * režimu vozidla v deň dokladu (ak caller nedodal explicitný). Vracia { event, projection }.
 *
 * @param {string} eventKey   deterministický kľúč operácie (idempotentný retry = no-op)
 * @param {string} vehicleId
 * @param {string} eventType  cost_recorded|cost_voided|cost_corrected
 * @param {string} costId     id nákladu v projekcii (pri cost_corrected = NOVÝ)
 * @param {object} payload    pre recorded/corrected: cost_type, price_base_cents, vat_rate,
 *                            doc_date (povinný), doc_number, attachment_hash, trip_id, liters
 * @param {string|object} actor
 */
export async function recordCostEvent({
  eventKey, vehicleId, eventType, costId, payload, actor,
} = {}) {
  if (!COST_PROJECTION_EVENTS.has(eventType)) {
    const known = TRIP_LEDGER_EVENT_TYPES.includes(eventType);
    throw new Error(
      `[v2/vehicleCosts] recordCostEvent nepodporuje event_type ${JSON.stringify(eventType)}`
      + (known ? ' (nie je cost event)' : ' (neznámy event)'),
    );
  }
  if (!costId || typeof costId !== 'string') {
    throw new Error(`[v2/vehicleCosts] costId je povinný, dostal ${JSON.stringify(costId)}`);
  }
  const isRecordLike = eventType === 'cost_recorded' || eventType === 'cost_corrected';
  const docDate = dateOnly(payload?.doc_date);
  if (isRecordLike) {
    if (!docDate) throw new Error(`[v2/vehicleCosts] ${eventType} vyžaduje platný doc_date (dátum dokladu)`);
    if (payload?.cost_type && !COST_TYPES.includes(payload.cost_type)) {
      throw new Error(`[v2/vehicleCosts] neznámy cost_type ${JSON.stringify(payload.cost_type)}`);
    }
  }
  if (eventType === 'cost_corrected' && payload?.corrects_id === costId) {
    throw new Error('[v2/vehicleCosts] cost_corrected: opravný náklad nemôže mať to isté id ako opravovaný');
  }

  return db.transaction('rw', db.vehicle_trip_ledger, db.vehicle_costs, db.vehicles, async (tx) => {
    const eventId = await tripLedgerEventId(eventKey);
    const existingCost = await tx.vehicle_costs.get(costId);

    // #5: costId je GLOBÁLNY PK projekcie — ak už patrí inému vozidlu, druhý zápis by
    // ticho prepísal cudzí náklad (strata dôkazu). Idempotency-safe: retry má rovnaké vehicleId.
    if (existingCost && existingCost.vehicle_id !== vehicleId) {
      throw new Error(`[v2/vehicleCosts] costId ${costId} už patrí inému vozidlu — id musí byť globálne unikátne`);
    }
    // #3: recorded/corrected musí niesť NOVÉ id. Ak costId už v tomto vozidle existuje a
    // NIE je to idempotentný retry tejto operácie (owner ledger event má iný id) → chyba
    // (inak by sa ledger zapísal, ale fold by opravný event ticho zahodil = nekonzistencia).
    if (isRecordLike && existingCost) {
      const led = await listTripLedger(vehicleId, tx);
      const owner = led.find((e) => e.trip_id === costId && COST_PROJECTION_EVENTS.has(e.event_type));
      if (owner && owner.id !== eventId) {
        throw new Error(`[v2/vehicleCosts] costId ${costId} už existuje — recorded/corrected vyžaduje nové id`);
      }
    }
    // #4: oprava musí referencovať EXISTUJÚCI stornovaný náklad rovnakého vozidla — inak by
    // vznikli dva uplatniteľné náklady (pôvodný nestornovaný + „oprava").
    if (eventType === 'cost_corrected') {
      const target = await tx.vehicle_costs.get(payload?.corrects_id);
      if (!target || target.vehicle_id !== vehicleId || target.status !== 'voided') {
        throw new Error(`[v2/vehicleCosts] cost_corrected.corrects_id musí byť existujúci STORNOVANÝ náklad rovnakého vozidla (dostal ${JSON.stringify(payload?.corrects_id)})`);
      }
    }

    // #1: deduction_pct je systémom určená (odvodená z režimu v deň dokladu) — NIKDY
    // z callera (inak by si sfalšoval 100 % na 50 % vozidle). Vždy prepočítaj a prepíš.
    let finalPayload = payload;
    if (isRecordLike) {
      const vehicle = await tx.vehicles.get(vehicleId);
      const deduction = vehicle ? resolveCostDeduction(vehicle, docDate) : 50;
      finalPayload = { ...payload, deduction_pct: deduction, doc_date: docDate };
    }

    const event = await appendTripEvent({
      eventKey, vehicleId, eventType, tripId: costId, payload: finalPayload, actor,
      occurredAt: docDate || firstIso(payload), conn: tx,
    });
    const projection = await syncProjectionFromLedger(vehicleId, tx);
    return { event, projection: projection.get(costId) ?? null };
  });
}

// occurredAt pre cost eventy = doc_date (stabilný deklarovaný dátum dokladu). Pre void
// (bez doc_date) caller dodá occurredAt cez payload.occurred_at (stabilný, nie Date.now()).
function firstIso(payload) {
  const c = dateOnly(payload?.occurred_at) || dateOnly(payload?.doc_date);
  if (!c) throw new Error('[v2/vehicleCosts] chýba stabilný čas eventu (doc_date alebo payload.occurred_at)');
  return c;
}

/**
 * rebuildCostProjection(vehicleId) — recovery: prepíš projekciu nákladov vozidla z
 * nemenného ledgeru. Soft-delete sirôt (nie fyzický delete — ledger princíp §3).
 */
export async function rebuildCostProjection(vehicleId) {
  if (!vehicleId || typeof vehicleId !== 'string') {
    throw new Error(`[v2/vehicleCosts] rebuildCostProjection vyžaduje vehicleId, dostal ${JSON.stringify(vehicleId)}`);
  }
  return db.transaction('rw', db.vehicle_trip_ledger, db.vehicle_costs, async (tx) => {
    const events = await listTripLedger(vehicleId, tx);
    const folded = foldCostEvents(events);
    // Id-y s oporou v LOKÁLNOM ledgeri (COST family — ledger zdieľaný s jazdami, filter nutný inak
    // by trip id falošne označil cudzí náklad za sirotu; Codex verify). Riadok mimo = CUDZÍ.
    const ledgerIds = new Set(
      events.filter((e) => COST_PROJECTION_EVENTS.has(e.event_type)).map((e) => e.trip_id).filter(Boolean),
    );
    const existing = (await tx.vehicle_costs.toArray()).filter((r) => r.vehicle_id === vehicleId);
    const seen = new Set();
    for (const [id, row] of folded) {
      const prev = await tx.vehicle_costs.get(id);
      seen.add(id);
      // Kolízia id s nákladom INÉHO vozidla → neklobrni (Codex verify, money-path).
      if (prev && prev.vehicle_id !== vehicleId) continue;
      if (rowDiffers(row, prev)) await tx.vehicle_costs.put(row);
    }
    // Soft-delete LEN lokálne-autorské siroty. Cudzie cross-device náklady NEMAŽ — delete by sa
    // syncol späť a zmazal platné náklady iného zariadenia (Daniel PRESERVE, B, 2026-07-27;
    // verifyCostProjection ich nahlási v `foreign`).
    for (const r of existing) {
      if (ledgerIds.has(r.id) && !seen.has(r.id) && !r.deleted_at) {
        await tx.vehicle_costs.put({ ...r, deleted_at: new Date().toISOString(), rev: (r.rev || 1) + 1 });
      }
    }
    return { rebuilt: folded.size };
  });
}

/**
 * verifyCostProjection(vehicleId) — read-only audit: projekcia vs fold ledgeru.
 */
export async function verifyCostProjection(vehicleId) {
  if (!vehicleId || typeof vehicleId !== 'string') {
    throw new Error(`[v2/vehicleCosts] verifyCostProjection vyžaduje vehicleId, dostal ${JSON.stringify(vehicleId)}`);
  }
  const events = await listTripLedger(vehicleId);
  const folded = foldCostEvents(events);
  // Filter podľa COST family — ledger zdieľaný s jazdami (Codex verify).
  const ledgerIds = new Set(
    events.filter((e) => COST_PROJECTION_EVENTS.has(e.event_type)).map((e) => e.trip_id).filter(Boolean),
  );
  const stored = (await db.vehicle_costs.toArray()).filter((r) => r.vehicle_id === vehicleId && !r.deleted_at);
  const mismatches = [];
  const foreign = [];
  for (const [id, row] of folded) {
    const prev = stored.find((r) => r.id === id);
    if (!prev) { mismatches.push({ cost_id: id, reason: 'chýba v projekcii' }); continue; }
    if (rowDiffers(row, prev)) mismatches.push({ cost_id: id, reason: 'projekcia nesedí s ledgerom' });
  }
  for (const r of stored) {
    if (folded.has(r.id)) continue;
    // Cudzí cross-device náklad (mimo lokálneho ledgeru) → nahlás, nelám ok (Daniel PRESERVE, B).
    if (ledgerIds.has(r.id)) mismatches.push({ cost_id: r.id, reason: 'projekcia navyše (má event v ledgeri, fold ju nedá)' });
    else foreign.push({ cost_id: r.id, reason: 'cudzí riadok — mimo lokálneho ledgeru (iné zariadenie)' });
  }
  return { ok: mismatches.length === 0, mismatches, foreign };
}

// listCosts(vehicleId?) — projekcia pre UI (bez soft-deleted), zoradená podľa seq_no.
export async function listCosts(vehicleId) {
  const rows = (await db.vehicle_costs.toArray()).filter((r) => !r.deleted_at);
  const filtered = vehicleId ? rows.filter((r) => r.vehicle_id === vehicleId) : rows;
  return filtered.sort((a, b) => {
    if (a.vehicle_id !== b.vehicle_id) return a.vehicle_id < b.vehicle_id ? -1 : 1;
    return (a.seq_no ?? 1e9) - (b.seq_no ?? 1e9);
  });
}
